/**
 * Feature Flag System for Pitch Deck Generation
 * 
 * This module provides utility functions for checking feature flag access
 * throughout the application. It supports both isolated testing mode
 * (specific users) and production mode (globally enabled).
 * 
 * @module pitch-deck/featureFlags
 */

import { supabase as defaultSupabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";

// =====================================================
// TYPE DEFINITIONS
// =====================================================

/**
 * Represents a feature flag from the database
 */
export interface FeatureFlag {
  id: string;
  feature_name: string;
  enabled: boolean;
  enabled_for_users: string[] | null;
  enabled_for_roles: string[] | null;
  description: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

/**
 * Result of checking feature access
 */
export interface FeatureAccessResult {
  /** Whether the user has access to the feature */
  hasAccess: boolean;
  /** Reason for access denial (if applicable) */
  reason?: string;
  /** True if user is a test user (in enabled_for_users), false if globally enabled */
  isIsolatedMode: boolean;
  /** The full feature flag object (if found) */
  flag?: FeatureFlag;
}

/**
 * Extended result for pitch deck access that includes prerequisite status
 */
export interface PitchDeckAccessResult extends FeatureAccessResult {
  /** Whether curriculum prerequisites are met */
  prerequisitesMet?: boolean;
}

/**
 * Result of admin operations
 */
export interface AdminOperationResult {
  success: boolean;
  error?: string;
}

// =====================================================
// CORE FEATURE FLAG FUNCTIONS
// =====================================================

/**
 * Check if a user has access to a specific feature
 * 
 * @example
 * ```typescript
 * const access = await checkFeatureAccess('pitch_deck_generation', user.id);
 * if (access.hasAccess) {
 *   // Show feature
 * } else {
 *   // Show "not available" message
 *   console.log('Access denied:', access.reason);
 * }
 * ```
 * 
 * @param featureName - The feature flag name (e.g., 'pitch_deck_generation')
 * @param userId - The user's UUID
 * @param supabaseClient - Optional Supabase client (uses default if not provided)
 * @returns Promise with access result
 */
export async function checkFeatureAccess(
  featureName: string,
  userId: string,
  supabaseClient?: SupabaseClient
): Promise<FeatureAccessResult> {
  const client = supabaseClient ?? defaultSupabase;

  try {
    // Use SECURITY DEFINER RPC to avoid exposing the full feature_flags row
    // (including enabled_for_users UUIDs) to non-admin authenticated users.
    const { data, error } = await client.rpc("check_user_feature_access", {
      _feature_name: featureName,
      _user_id: userId,
    });

    if (error) {
      console.error("Error checking feature access:", error);
      return {
        hasAccess: false,
        reason: "Database error",
        isIsolatedMode: false,
      };
    }

    const row = Array.isArray(data) ? data[0] : data;

    if (!row || row.feature_exists === false) {
      return {
        hasAccess: false,
        reason: "Feature not found",
        isIsolatedMode: false,
      };
    }

    if (row.has_access) {
      return {
        hasAccess: true,
        isIsolatedMode: !!row.is_isolated_mode,
      };
    }

    return {
      hasAccess: false,
      reason: "Feature not enabled for this user",
      isIsolatedMode: false,
    };
  } catch (error) {
    console.error("Unexpected error checking feature access:", error);
    return {
      hasAccess: false,
      reason: "Unexpected error",
      isIsolatedMode: false,
    };
  }
}

/**
 * Check pitch deck feature access with prerequisite validation
 * 
 * This is a specialized function for the pitch deck feature that:
 * 1. Checks feature flag access
 * 2. Validates curriculum prerequisites (mock or real based on mode)
 * 
 * @example
 * ```typescript
 * const access = await checkPitchDeckAccess(user.id);
 * 
 * if (!access.hasAccess) {
 *   return <FeatureNotAvailable />;
 * }
 * 
 * if (access.isIsolatedMode) {
 *   return <TestModeBanner />;
 * }
 * 
 * if (!access.prerequisitesMet) {
 *   return <PrerequisitesNotMet />;
 * }
 * 
 * return <PitchDeckForms />;
 * ```
 * 
 * @param userId - The user's UUID
 * @param supabaseClient - Optional Supabase client
 * @returns Promise with access result including prerequisite status
 */
export async function checkPitchDeckAccess(
  userId: string,
  supabaseClient?: SupabaseClient
): Promise<PitchDeckAccessResult> {
  const client = supabaseClient ?? defaultSupabase;

  // Step 1: Check feature flag access
  const accessResult = await checkFeatureAccess(
    "pitch_deck_generation",
    userId,
    client
  );

  // If no access, return immediately
  if (!accessResult.hasAccess) {
    return {
      ...accessResult,
      prerequisitesMet: undefined,
    };
  }

  // Step 2: Check prerequisites based on mode
  let prerequisitesMet: boolean;

  if (accessResult.isIsolatedMode) {
    // Isolated/test mode - use mock curriculum progress
    prerequisitesMet = await checkMockPrerequisites(userId, client);
  } else {
    // Production mode - use real LMS data
    prerequisitesMet = await checkRealPrerequisites(userId, client);
  }

  return {
    ...accessResult,
    prerequisitesMet,
  };
}

// =====================================================
// PREREQUISITE CHECKING FUNCTIONS
// =====================================================

/**
 * Check mock curriculum progress for testing (isolated mode)
 * 
 * In isolated testing mode, we use the mock_curriculum_progress table
 * instead of the real LMS tables. This allows testing without affecting
 * or depending on the production LMS.
 * 
 * @param userId - The user's UUID
 * @param supabaseClient - Supabase client to use
 * @returns Promise<boolean> - true if all prerequisites are met
 */
export async function checkMockPrerequisites(
  userId: string,
  supabaseClient: SupabaseClient
): Promise<boolean> {
  try {
    // Query mock_curriculum_progress table
    const { data: progress, error } = await supabaseClient
      .from("mock_curriculum_progress")
      .select("*")
      .eq("user_id", userId)
      .single();

    if (error && error.code !== "PGRST116") {
      // PGRST116 = no rows found, which is okay
      console.error("Error fetching mock prerequisites:", error);
      return false;
    }

    // If no record found, auto-create one with all modules complete
    // This makes testing easier for new test users
    if (!progress) {
      console.log("No mock progress found, auto-creating for test user:", userId);
      
      const { error: insertError } = await supabaseClient
        .from("mock_curriculum_progress")
        .insert({
          user_id: userId,
          module_1_complete: true,
          module_2_complete: true,
          module_3_complete: true,
          badge_awarded: false,
        });

      if (insertError) {
        console.error("Error auto-creating mock progress:", insertError);
        return false;
      }

      return true;
    }

    // Check if all three modules are complete
    const allComplete = 
      progress.module_1_complete === true &&
      progress.module_2_complete === true &&
      progress.module_3_complete === true;

    return allComplete;
  } catch (error) {
    console.error("Unexpected error checking mock prerequisites:", error);
    return false;
  }
}

/**
 * Check real LMS curriculum progress (placeholder for production)
 * 
 * This function will be implemented when we connect to the real LMS.
 * For now, it returns true to allow development to proceed.
 * 
 * @param userId - The user's UUID
 * @param supabaseClient - Supabase client to use
 * @returns Promise<boolean> - true if all prerequisites are met
 */
export async function checkRealPrerequisites(
  userId: string,
  supabaseClient: SupabaseClient
): Promise<boolean> {
  // TODO: Implement real LMS prerequisite checking
  // This will query the actual course_enrollments, module_progress, etc.
  console.warn(
    "Real prerequisite checking not implemented yet. User:",
    userId,
    "Returning true for now."
  );
  
  // Placeholder: return true to allow development
  return true;
}

// =====================================================
// ADMIN FUNCTIONS (Service Role Required)
// =====================================================

/**
 * Add users to the feature flag allowlist
 * 
 * ⚠️ ADMIN ONLY: This function requires service role key.
 * It should only be called from secure backend contexts (Edge Functions).
 * 
 * @example
 * ```typescript
 * // In an Edge Function with service role
 * const result = await enableFeatureForUsers(
 *   'pitch_deck_generation',
 *   ['user-id-1', 'user-id-2'],
 *   supabaseAdmin
 * );
 * ```
 * 
 * @param featureName - The feature flag name
 * @param userIds - Array of user UUIDs to enable
 * @param supabaseClient - Supabase client with service role
 * @returns Promise with operation result
 */
export async function enableFeatureForUsers(
  featureName: string,
  userIds: string[],
  supabaseClient: SupabaseClient
): Promise<AdminOperationResult> {
  try {
    const { error } = await supabaseClient
      .from("feature_flags")
      .update({ enabled_for_users: userIds })
      .eq("feature_name", featureName);

    if (error) {
      console.error("Error enabling feature for users:", error);
      return { success: false, error: error.message };
    }

    console.log(`Feature '${featureName}' enabled for ${userIds.length} users`);
    return { success: true };
  } catch (error) {
    console.error("Unexpected error enabling feature for users:", error);
    return { success: false, error: "Unexpected error" };
  }
}

/**
 * Add a single user to the feature flag allowlist (preserving existing users)
 * 
 * ⚠️ ADMIN ONLY: This function requires service role key.
 * 
 * @param featureName - The feature flag name
 * @param userId - User UUID to add
 * @param supabaseClient - Supabase client with service role
 * @returns Promise with operation result
 */
export async function addUserToFeature(
  featureName: string,
  userId: string,
  supabaseClient: SupabaseClient
): Promise<AdminOperationResult> {
  try {
    // First, get current users
    const { data: flag, error: fetchError } = await supabaseClient
      .from("feature_flags")
      .select("enabled_for_users")
      .eq("feature_name", featureName)
      .single();

    if (fetchError) {
      console.error("Error fetching feature flag:", fetchError);
      return { success: false, error: fetchError.message };
    }

    // Add user to array (avoid duplicates)
    const currentUsers = (flag?.enabled_for_users as string[]) ?? [];
    if (currentUsers.includes(userId)) {
      return { success: true }; // Already in list
    }

    const updatedUsers = [...currentUsers, userId];

    // Update the flag
    const { error: updateError } = await supabaseClient
      .from("feature_flags")
      .update({ enabled_for_users: updatedUsers })
      .eq("feature_name", featureName);

    if (updateError) {
      console.error("Error adding user to feature:", updateError);
      return { success: false, error: updateError.message };
    }

    console.log(`User '${userId}' added to feature '${featureName}'`);
    return { success: true };
  } catch (error) {
    console.error("Unexpected error adding user to feature:", error);
    return { success: false, error: "Unexpected error" };
  }
}

/**
 * Enable a feature globally for all users
 * 
 * ⚠️ ADMIN ONLY: This function requires service role key.
 * This is the production rollout switch.
 * 
 * @param featureName - The feature flag name
 * @param supabaseClient - Supabase client with service role
 * @returns Promise with operation result
 */
export async function enableFeatureGlobally(
  featureName: string,
  supabaseClient: SupabaseClient
): Promise<AdminOperationResult> {
  try {
    const { error } = await supabaseClient
      .from("feature_flags")
      .update({ enabled: true })
      .eq("feature_name", featureName);

    if (error) {
      console.error("Error enabling feature globally:", error);
      return { success: false, error: error.message };
    }

    console.log(`Feature '${featureName}' enabled globally`);
    return { success: true };
  } catch (error) {
    console.error("Unexpected error enabling feature globally:", error);
    return { success: false, error: "Unexpected error" };
  }
}

/**
 * Emergency killswitch - disable a feature for everyone immediately
 * 
 * ⚠️ ADMIN ONLY: This function requires service role key.
 * Use this in case of critical issues requiring instant rollback.
 * 
 * This function:
 * - Sets enabled = false
 * - Clears enabled_for_users array
 * 
 * @param featureName - The feature flag name
 * @param supabaseClient - Supabase client with service role
 * @returns Promise with operation result
 */
export async function disableFeature(
  featureName: string,
  supabaseClient: SupabaseClient
): Promise<AdminOperationResult> {
  try {
    const { error } = await supabaseClient
      .from("feature_flags")
      .update({
        enabled: false,
        enabled_for_users: [],
      })
      .eq("feature_name", featureName);

    if (error) {
      console.error("Error disabling feature:", error);
      return { success: false, error: error.message };
    }

    console.warn(`🚨 KILLSWITCH: Feature '${featureName}' has been disabled`);
    return { success: true };
  } catch (error) {
    console.error("Unexpected error disabling feature:", error);
    return { success: false, error: "Unexpected error" };
  }
}

// =====================================================
// UTILITY FUNCTIONS
// =====================================================

/**
 * Get the current status of a feature flag
 * 
 * @param featureName - The feature flag name
 * @param supabaseClient - Optional Supabase client
 * @returns Promise with the feature flag or null
 */
export async function getFeatureFlag(
  featureName: string,
  supabaseClient?: SupabaseClient
): Promise<FeatureFlag | null> {
  const client = supabaseClient ?? defaultSupabase;

  try {
    const { data, error } = await client
      .from("feature_flags")
      .select("*")
      .eq("feature_name", featureName)
      .single();

    if (error) {
      console.error("Error fetching feature flag:", error);
      return null;
    }

    return data as unknown as FeatureFlag;
  } catch (error) {
    console.error("Unexpected error fetching feature flag:", error);
    return null;
  }
}

/**
 * Check if the current user is in isolated testing mode for a feature
 * 
 * @param featureName - The feature flag name
 * @param userId - The user's UUID
 * @param supabaseClient - Optional Supabase client
 * @returns Promise<boolean> - true if user is a test user
 */
export async function isUserInTestMode(
  featureName: string,
  userId: string,
  supabaseClient?: SupabaseClient
): Promise<boolean> {
  const access = await checkFeatureAccess(featureName, userId, supabaseClient);
  return access.hasAccess && access.isIsolatedMode;
}
